import React from "react";
import { Link } from "react-router-dom";
import { MapPin, Globe, CheckCircle2, Wifi, Users, UserCheck } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";
import { fileUrl } from "../lib/api";
import { PROFESSIONS, localize, countryLabel, professionCategoryLabel, specialtyLabel } from "../lib/meta";
import { useLanguageLabeler } from "../lib/taxonomy";

export function PsychologistCard({ p }) {
  const { t, lang } = useLang();
  const langLabel = useLanguageLabeler();
  const photo = fileUrl(p.photo_path);
  const professionLabel = (p.profession === "other_clinical" || p.profession === "other_non_clinical") && p.profession_other
    ? p.profession_other
    : p.profession ? localize(PROFESSIONS, p.profession, lang) : t("profile.professional_fallback");
  const isNonClinical = p.profession_category === "non_clinical";
  const formatLabel =
    p.service_format === "online" ? t("common.online")
    : p.service_format === "in_person" ? t("common.in_person")
    : t("common.both");

  return (
    <Link
      to={`/profesionales/${p.id}`}
      data-testid={`psychologist-card-${p.id}`}
      className="group flex flex-col rounded-2xl border border-border bg-white p-6 transition-all hover:-translate-y-0.5 hover:border-terracotta/40 hover:shadow-[0_8px_30px_rgba(200,109,81,0.08)]"
    >
      <div className="flex items-start gap-4">
        <div className="h-16 w-16 shrink-0 overflow-hidden rounded-xl bg-secondary">
          {photo ? (
            <img src={photo} alt={p.full_name} className="h-full w-full object-cover" />
          ) : (
            <div className="flex h-full w-full items-center justify-center font-display text-xl font-bold text-slateink/40">
              {(p.full_name || "?").charAt(0)}
            </div>
          )}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5">
            <h3 className="truncate font-display text-lg font-semibold tracking-tight text-slateink">{p.full_name}</h3>
            {p.status === "verified" && <CheckCircle2 className="h-4 w-4 shrink-0 text-[#1E6B47]" title={t("profile.verified_note")} />}
          </div>
          <div className="mt-1 flex flex-wrap items-center gap-1.5">
            {p.profession_group && <span className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground" data-testid={`card-category-${p.id}`}>{professionCategoryLabel(p.profession_group, lang)}</span>}
          </div>
          <div className="mt-1 flex flex-wrap items-center gap-1.5">
            <span data-testid={`card-profession-${p.id}`} className="rounded-full bg-slateink px-2 py-0.5 text-[11px] font-semibold text-white">{professionLabel}</span>
            {!isNonClinical && <span className="rounded-full bg-[#EBF6F0] px-2 py-0.5 text-[11px] font-medium text-[#1E6B47]">{t("profile.clinical")}</span>}
          </div>
          {p.title && <p className="mt-1 truncate text-sm text-muted-foreground">{p.title}</p>}
          {(p.city || p.country) && (
            <p className="mt-1 flex items-center gap-1 text-xs text-muted-foreground">
              <MapPin className="h-3.5 w-3.5" /> {[p.city, countryLabel(p.country, lang)].filter(Boolean).join(", ")}
            </p>
          )}
        </div>
      </div>

      {p.languages?.length > 0 && (
        <p className="mt-4 flex items-center gap-1.5 text-xs text-muted-foreground">
          <Globe className="h-3.5 w-3.5" /> {p.languages.map((l) => langLabel(l, lang)).join(" · ")}
        </p>
      )}

      {(p.specialties?.length > 0 ? p.specialties : p.areas)?.length > 0 && (
        <div className="mt-4 flex flex-wrap gap-1.5">
          {(p.specialties?.length > 0 ? p.specialties : p.areas).slice(0, 3).map((a) => (
            <span key={a} className="rounded-full bg-secondary px-2.5 py-1 text-xs font-medium text-secondary-foreground">{p.specialties?.length > 0 ? specialtyLabel(a, lang) : a}</span>
          ))}
          {(p.specialties?.length > 0 ? p.specialties : p.areas).length > 3 && <span className="px-1 py-1 text-xs text-muted-foreground">+{(p.specialties?.length > 0 ? p.specialties : p.areas).length - 3}</span>}
        </div>
      )}

      <div className="mt-5 flex flex-wrap items-center gap-2 border-t border-border pt-4 text-xs">
        <span className="inline-flex items-center gap-1 text-muted-foreground"><Wifi className="h-3.5 w-3.5" /> {formatLabel}</span>
        {p.accepting_new_clients && (
          <span className="inline-flex items-center gap-1 rounded-full bg-[#EBF6F0] px-2.5 py-1 font-medium text-[#1E6B47]"><UserCheck className="h-3 w-3" /> {t("profile.accepting_new")}</span>
        )}
        {(p.service_type === "volunteer" || p.service_type === "both") && (
          <span className="rounded-full bg-[#EFEFFD] px-2.5 py-1 font-medium text-[#3D3893]">{t("common.volunteer")}</span>
        )}
        {(p.service_type === "paid" || p.service_type === "both") && (
          <span className="rounded-full bg-[#F5F3EF] px-2.5 py-1 font-medium text-[#5A544B]">{t("common.paid")}</span>
        )}
        {p.available_for_initiatives && (
          <span className="inline-flex items-center gap-1 rounded-full bg-terracotta-light px-2.5 py-1 font-medium text-terracotta"><Users className="h-3 w-3" /> {t("profile.initiatives")}</span>
        )}
      </div>
    </Link>
  );
}
